// AE 打包檔（0053）的前端呼叫 — 對應 cf-worker/aePackages.mjs。API 文件：docs/ae-packages-api.md
//
// 上傳跟影片一樣是分段（每段 16 MiB，一段失敗只重傳那一段，最多重試 3 次）；沒按「完成」的不算數。
// 權限（後台會檢查，畫面只是先擋）：看清單 / 下載 = 製作 + 這個專案的動畫師；上傳 / 換 / 刪 = 製作。

import { apiBase, getSessionToken } from './api';
import { isViewAsBlocked, VIEW_AS_BLOCKED_MESSAGE } from './viewAs';
import type { AePackageStale } from './aePackage';

export interface AePackage {
  id: string;
  /** led_templates.id；「全部」= kind 'full' 那一列的 id */
  templateId: string;
  filename: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: { id: string; name: string | null } | null;
  /** 上傳那一刻的製作尺寸（'1/2'；原尺寸 = null） */
  workScale: string | null;
  canvas: { width: number; height: number } | null;
  stale: AePackageStale;
  /** 相對路徑（absoluteAePackageUrl 接上後台網址），已經帶了 6～12 小時的下載通行證 */
  downloadUrl: string;
}

export interface AePackageList { packages: AePackage[]; workScale: string | null; maxBytes: number }

export interface AePackageUpload {
  id: string; templateId: string; filename: string; sizeBytes: number; partSize: number; partCount: number; uploadedParts: number[];
}

export interface AePackageApiError extends Error {
  status: number;
  data: { error?: string; maxBytes?: number; missingParts?: number[]; [k: string]: unknown };
}

const enc = encodeURIComponent;
const base = (projectId: string) => `/api/projects/${enc(projectId)}/ae-packages`;

function makeError(status: number, data: AePackageApiError['data']): AePackageApiError {
  const e = new Error(data?.error || `HTTP ${status}`) as AePackageApiError;
  e.status = status;
  e.data = data || {};
  return e;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (init.body) headers['Content-Type'] = 'application/json';
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(apiBase() + path, { credentials: 'include', ...init, headers });
  if (!resp.ok) {
    let data: AePackageApiError['data'] = {};
    try { data = await resp.json(); } catch { /* 不是 JSON */ }
    throw makeError(resp.status, data);
  }
  return resp.json() as Promise<T>;
}

export function absoluteAePackageUrl(downloadUrl: string): string {
  return /^https?:\/\//.test(downloadUrl) ? downloadUrl : apiBase() + downloadUrl;
}

/** 這個專案的打包檔清單；舊版後台沒有這條路（404）→ null（畫面不顯示這個功能） */
export async function listAePackages(projectId: string): Promise<AePackageList | null> {
  try {
    return await call<AePackageList>(base(projectId));
  } catch (e) {
    if ((e as AePackageApiError).status === 404) return null;
    throw e;
  }
}

export async function startAePackageUpload(projectId: string, input: { templateId: string; filename: string; sizeBytes: number }): Promise<AePackageUpload> {
  return (await call<{ upload: AePackageUpload }>(`${base(projectId)}/uploads`, { method: 'POST', body: JSON.stringify(input) })).upload;
}

export async function completeAePackageUpload(projectId: string, uploadId: string): Promise<{ replaced?: boolean }> {
  return call(`${base(projectId)}/uploads/${enc(uploadId)}/complete`, { method: 'POST' });
}

export async function abortAePackageUpload(projectId: string, uploadId: string): Promise<void> {
  await call(`${base(projectId)}/uploads/${enc(uploadId)}`, { method: 'DELETE' });
}

export async function deleteAePackage(projectId: string, packageId: string): Promise<void> {
  await call(`${base(projectId)}/${enc(packageId)}`, { method: 'DELETE' });
}

/** 傳一段（XMLHttpRequest 才有進度）；失敗 → AePackageApiError（有 status）或網路斷 TypeError */
export function uploadAePackagePart(
  projectId: string, uploadId: string, partNumber: number, chunk: Blob, onProgress?: (loaded: number) => void, signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', apiBase() + `${base(projectId)}/uploads/${enc(uploadId)}/parts/${partNumber}`);
    xhr.withCredentials = true;
    const token = getSessionToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    if (onProgress) xhr.upload.onprogress = (e) => onProgress(Math.min(e.loaded, chunk.size));
    let settled = false;
    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      fn();
    };
    xhr.onload = () => finish(() => {
      let data: AePackageApiError['data'] = {};
      try { data = JSON.parse(xhr.responseText || '{}') ?? {}; } catch { /* 不是 JSON */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(makeError(xhr.status, data));
    });
    xhr.onerror = () => finish(() => reject(new TypeError('Failed to fetch')));
    xhr.ontimeout = () => finish(() => reject(new TypeError('Failed to fetch')));
    xhr.onabort = () => finish(() => reject(new DOMException('Aborted', 'AbortError')));
    xhr.send(chunk);
  });
}

export interface UploadAePackageOptions {
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
  /** 一段最多試幾次（預設 3） */
  attempts?: number;
  /** 測試用：不真的等 */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/** 值得重試的錯誤：網路斷、後台 5xx；4xx（檔案不對、沒權限）重試也沒用 */
export function isRetryablePartError(e: unknown): boolean {
  if (e instanceof DOMException && e.name === 'AbortError') return false;
  const status = (e as { status?: unknown } | null)?.status;
  return typeof status !== 'number' || status >= 500;
}

/**
 * 整包上傳：開始 → 一段一段傳（失敗的段重試）→ 完成。任何一步失敗就把這次上傳放棄（現有的包不動），並把錯誤丟出去。
 * 沒傳完的不算數：只有「完成」成功才會換掉現有的包。
 */
export async function uploadAePackage(
  projectId: string, templateId: string, file: File, opts: UploadAePackageOptions = {},
): Promise<{ replaced: boolean }> {
  const { signal, onProgress } = opts;
  const attempts = Math.max(1, opts.attempts ?? 3);
  const sleep = opts.sleep ?? defaultSleep;
  const upload = await startAePackageUpload(projectId, { templateId, filename: file.name, sizeBytes: file.size });
  let confirmed = 0;
  try {
    for (let n = 1; n <= upload.partCount; n++) {
      const start = (n - 1) * upload.partSize;
      const chunk = file.slice(start, Math.min(file.size, start + upload.partSize));
      for (let attempt = 1; ; attempt++) {
        try {
          await uploadAePackagePart(projectId, upload.id, n, chunk, (loaded) => onProgress?.(confirmed + loaded, file.size), signal);
          break;
        } catch (e) {
          if (attempt >= attempts || !isRetryablePartError(e)) throw e;
          await sleep(800 * attempt);
        }
      }
      confirmed += chunk.size;
      onProgress?.(Math.min(confirmed, file.size - 1), file.size);   // 後台還沒說完成之前停在 99%
    }
    const done = await completeAePackageUpload(projectId, upload.id);
    onProgress?.(file.size, file.size);
    return { replaced: !!done.replaced };
  } catch (e) {
    // 放棄這次（不在預覽模式被擋的情況下才有東西可放棄；放棄失敗不影響原本的錯誤）
    if (!isViewAsBlocked(e)) { try { await abortAePackageUpload(projectId, upload.id); } catch { /* 之後排程會清 */ } }
    throw e;
  }
}

/** 上傳 / 刪除失敗要跟人說什麼（後台的中文照用） */
export function aePackageErrorText(e: unknown): string {
  if (isViewAsBlocked(e)) return VIEW_AS_BLOCKED_MESSAGE;
  if (e instanceof DOMException && e.name === 'AbortError') return '已取消上傳';
  const status = (e as { status?: unknown } | null)?.status;
  if (typeof status === 'number') {
    const msg = (e as Error).message;
    if (msg && !/^HTTP \d+$/.test(msg)) return msg;
    if (status === 401) return '登入過期了，請重新整理頁面再登入';
    if (status === 403) return '只有 PM 可以上傳、換或刪 AE 打包檔';
    if (status === 409) return '這個專案已封存或這一包剛剛被換掉了，請重新整理再試';
    return `伺服器回應錯誤（${status}），請稍後再試`;
  }
  return '網路斷了，打包檔沒有傳完，請檢查網路後再試一次';
}
